import styled from "styled-components";
import { useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import ModalItensPc from "./ModalItensPc";

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px 16px;
    margin-bottom: 18px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--ctrl-label, #94a3b8);
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: var(--ctrl-text, #1f2937);
    font-weight: 500;
`;

const SectionTitle = styled.h3`
    margin: 18px 0 8px 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #334155);
    font-weight: 700;
`;

const Hint = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: var(--ctrl-label, #94a3b8);
    margin-left: 8px;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-height: 28dvh;
    overflow: auto;
    border: 1px solid var(--ctrl-border, #ddd);
    border-radius: 8px;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 12px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #666);
`;

const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Coluna = styled.td`
    text-align: ${({ align }) => align || "left"};
`;

const TotalRow = styled.tr`
    background-color: var(--ctrl-surface-soft, #f8fafc);
    font-weight: 700;

    & td {
        border-top: 2px solid var(--ctrl-border-soft, #e2e8f0);
    }
`;

const LinhaPc = styled.tr`
    cursor: ${(props) => (props.$clicavel ? "pointer" : "default")};
    opacity: ${(props) => (props.$desabilitada ? 0.55 : 1)};
    background-color: ${(props) => (props.$destaque ? "#ecfdf5" : "transparent")};

    &:hover {
        background-color: ${(props) =>
            props.$clicavel ? (props.$destaque ? "#d1fae5" : "#f1f5f9") : "transparent"};
    }
`;

const Badge = styled.span`
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    background-color: ${(props) => {
        if (props.$tipo === "vinculado") return "#dbeafe";
        if (props.$tipo === "parcial") return "#fef9c3";
        if (props.$tipo === "finalizado") return "#f1f5f9";
        return "#fef9c3";
    }};
    color: ${(props) => {
        if (props.$tipo === "vinculado") return "#1e40af";
        if (props.$tipo === "parcial") return "#854d0e";
        if (props.$tipo === "finalizado") return "#64748b";
        return "#854d0e";
    }};
`;

const VinculosBox = styled.div`
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    font-size: 13px;
    color: #1e40af;
`;

const formatQuantidade = (value) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (value == null || isNaN(numValue)) return " - ";
    return numValue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ModalSelecionarPc = ({ fecharModal, detalhes, loading, onVinculado }) => {
    const [pcAberto, setPcAberto] = useState(null);

    const nf = detalhes?.nf || null;
    const itensNf = detalhes?.itens_nf || [];
    const pedidos = detalhes?.pedidos || [];
    const vinculos = detalhes?.vinculos || [];

    const totalNf = itensNf.reduce((acc, item) => acc + (Number(item.VALOR_TOTAL) || 0), 0);

    const handleVinculado = () => {
        onVinculado?.();
        setPcAberto(null);
        fecharModal();
    };

    const abrirPc = (pedido) => {
        if (!pedido.PODE_VINCULAR) return;
        setPcAberto(pedido);
    };

    return (
        <>
            <Overlay state={true}>
                <Modal size="lg" header close={fecharModal}>
                    <Title>Vincular NF a Pedido de Compra</Title>

                    {loading ? (
                        <LoadingWrapper>
                            <SmallLoading text="Carregando informações da NF..." />
                        </LoadingWrapper>
                    ) : !nf ? (
                        <EmptyText>Não foi possível carregar os dados da NF.</EmptyText>
                    ) : (
                        <>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>Filial</InfoLabel>
                                    <InfoValue>{nf.FILIAL || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Nota Fiscal</InfoLabel>
                                    <InfoValue>{nf.NUM_NF || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Data Emissão</InfoLabel>
                                    <InfoValue>{nf.DT_NF || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Dt. Digitação Pré-nota</InfoLabel>
                                    <InfoValue>
                                        {nf.TEM_PRE_NOTA ? nf.DT_PRE_NOTA : "Não lançada"}
                                    </InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Fornecedor</InfoLabel>
                                    <InfoValue>{nf.FORNECEDOR || " - "}</InfoValue>
                                </InfoItem>
                            </InfoGrid>

                            {vinculos.length > 0 && (
                                <VinculosBox>
                                    <strong>Já vinculada a:</strong>{" "}
                                    {vinculos.map((v, i) => (
                                        <span key={v.num_pedido}>
                                            {i > 0 && ", "}
                                            PC {v.num_pedido}
                                            {v.status_pc ? ` (${v.status_pc})` : ""}
                                        </span>
                                    ))}
                                </VinculosBox>
                            )}

                            <SectionTitle>Itens da Nota Fiscal</SectionTitle>
                            {itensNf.length === 0 ? (
                                <EmptyText>Nenhum item encontrado nesta NF.</EmptyText>
                            ) : (
                                <TableWrapper>
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                <th>ITEM</th>
                                                <th>CÓD. FORN.</th>
                                                <th>DESCRIÇÃO</th>
                                                <th>NCM</th>
                                                <th>UNID.</th>
                                                <th>QTD.</th>
                                                <th>V. TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itensNf.map((item, index) => (
                                                <tr key={`${item.NITEM}-${index}`}>
                                                    <Coluna align="center">{item.NITEM}</Coluna>
                                                    <td>{item.COD_FORNEC || " - "}</td>
                                                    <td>{item.DESCRICAO || " - "}</td>
                                                    <Coluna align="center">{item.NCM || " - "}</Coluna>
                                                    <Coluna align="center">{item.UNIDADE || " - "}</Coluna>
                                                    <Coluna align="right">{formatQuantidade(item.QUANTIDADE)}</Coluna>
                                                    <Coluna align="right">
                                                        {item.VALOR_TOTAL != null
                                                            ? `R$ ${formatCurrency(item.VALOR_TOTAL)}`
                                                            : " - "}
                                                    </Coluna>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <TotalRow>
                                                <td colSpan={6}>Valor total da NF</td>
                                                <Coluna align="right">{`R$ ${formatCurrency(totalNf)}`}</Coluna>
                                            </TotalRow>
                                        </tfoot>
                                    </DataTable>
                                </TableWrapper>
                            )}

                            <SectionTitle>
                                Pedidos de Compra Candidatos
                                <Hint>Clique em um pedido para analisar e vincular</Hint>
                            </SectionTitle>
                            {pedidos.length === 0 ? (
                                <EmptyText>Nenhum pedido de compra encontrado para este fornecedor.</EmptyText>
                            ) : (
                                <TableWrapper>
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                <th>FILIAL</th>
                                                <th>Nº PEDIDO</th>
                                                <th>DATA EMISSÃO</th>
                                                <th>USUÁRIO</th>
                                                <th>VALOR TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pedidos.map((pedido, index) => (
                                                <LinhaPc
                                                    key={`${pedido.NUM_PEDIDO}-${index}`}
                                                    $clicavel={pedido.PODE_VINCULAR}
                                                    $desabilitada={!pedido.PODE_VINCULAR}
                                                    $destaque={pedido.PODE_VINCULAR}
                                                    onClick={() => abrirPc(pedido)}
                                                    title={
                                                        pedido.JA_VINCULADO_NF
                                                            ? "Este pedido já está vinculado a esta NF"
                                                            : pedido.FINALIZADO
                                                            ? "Pedido finalizado"
                                                            : "Clique para analisar e vincular"
                                                    }
                                                >
                                                    <td>{pedido.FILIAL}</td>
                                                    <td>
                                                        {pedido.NUM_PEDIDO}
                                                        {pedido.JA_VINCULADO_NF && (
                                                            <Badge $tipo="vinculado">Vinculado</Badge>
                                                        )}
                                                        {!pedido.JA_VINCULADO_NF && pedido.STATUS_PC === "PARCIAL" && (
                                                            <Badge $tipo="parcial">Parcial</Badge>
                                                        )}
                                                        {pedido.FINALIZADO && (
                                                            <Badge $tipo="finalizado">Finalizado</Badge>
                                                        )}
                                                    </td>
                                                    <Coluna align="center">{pedido.DT_EMISSAO || " - "}</Coluna>
                                                    <td>{pedido.USUARIO || " - "}</td>
                                                    <Coluna align="right">
                                                        {pedido.VALOR_TOTAL != null
                                                            ? `R$ ${formatCurrency(pedido.VALOR_TOTAL)}`
                                                            : " - "}
                                                    </Coluna>
                                                </LinhaPc>
                                            ))}
                                        </tbody>
                                    </DataTable>
                                </TableWrapper>
                            )}
                        </>
                    )}
                </Modal>
            </Overlay>

            {pcAberto && nf && (
                <ModalItensPc
                    nf={nf}
                    pedido={pcAberto}
                    onClose={() => setPcAberto(null)}
                    onVinculado={handleVinculado}
                />
            )}
        </>
    );
};

export default ModalSelecionarPc;
